// The room as this client sees it: seats with their colours, and the bubbles of the live canvas.
// A pure reducer over protocol messages (P-4..P-10, P-18, D-24). Time enters only through the actions
// (`now` is the injected clock, `wall` is the wall clock for the faint time on a bubble).
import { COMPARE_ABSENT_MS, TRANSLATION_WAIT_MS, TRANSLATION_WINDOW } from "../constants";
import { sameLanguage } from "../data/languages";
import type {
  ComparePhraseData,
  EarlierPhrase,
  Listing,
  ListenMode,
  RoomEvent,
  RoomStateData,
  Seat,
  SegmentData,
  TranslationData,
} from "../protocol/types";

export const SEAT_COLOURS = 8;

export interface SeatView extends Seat {
  /** Colour slot 0..7: join order as this client saw it, stable for the room's life, never reassigned. */
  colour: number;
  present: boolean;
}

export type Phase = "speaking" | "writing" | "landed" | "failed" | "dropped";

export interface Bubble {
  id: string;
  seat: string;
  t0: number; // room time, s: the canvas is ordered by t0, not by arrival (D-24)
  phase: Phase;
  startedAt: number; // local clock, ms: speech_start arrived
  endedAt?: number; // local clock, ms: speech_end arrived
  durationMs?: number; // speech_end.duration
  segment?: SegmentData;
  revised: boolean; // a revision with changed=true arrived (EM-20)
  revisionCount: number; // changed revisions: restarts the shimmer
  /** false for a segment that landed without a live bubble (it slides up, UI-M1). */
  hadLive: boolean;
  wall?: number; // wall-clock ms at t0, for the faint time
  /** MAJLIS-12, UI-C5, EM-23: the phrase's recording is stored (audio_ready arrived for THIS segment); its length in
   * ms. Undefined until then: the ReplayButton stays disabled. */
  audioMs?: number;
  /** MAJLIS-49 (D-17): local clock, ms, when the text became FINAL (a final segment landed, or its revision). */
  finalAt?: number;
  /** MAJLIS-49: 1, 2, 3... in the order segments landed on this client: the backfill window is the last 20. */
  landedIndex?: number;
  /** MAJLIS-73: local clock, ms, when a segment_dropped{other_speaker} started this bubble's fade (D-24 ruling 2).
   * Undefined for every other drop, which collapses at once (EM-11). */
  fadeFrom?: number;
  /** P-19 (MAJLIS-89): one of the phrases the join was handed (joined.earlier) - read-only history above the live
   * edge, drawn without the landing animation. */
  earlier?: boolean;
}

export interface RoomModel {
  seats: Record<string, SeatView>;
  seatOrder: string[]; // every seat ever seen, in join order; the index is the colour slot
  bubbles: Record<string, Bubble>;
  audioMode: "live" | "screen_only";
  maxSeats: number;
  mySeatId: string | null;
  roomWallEpoch: number | null; // wall-clock ms at room time 0
  landedSeq: number; // count of segments that landed, for "↓ n new"
  clockAt: number; // the injected clock at the latest event: stages are read at max(now, clockAt)
  /** UI-C20: whether the room's writing is running (room_state.engine on join/resume, then engine_status). */
  engine: "up" | "down";
  /** P-10 (MAJLIS-49): segment id -> reading language -> text, as the server sent them (DM-4). */
  translations: Record<string, Record<string, string>>;
  /** UI-C9: local clock, ms, of this seat's last reading-language change in this room (null: none yet). */
  readSince: number | null;
  /** MAJLIS-63 (UI-C17): how many audio_mode_changed events this client has applied. Each one shows the mode notice
   * once; room_state (a join, a resume's state) sets the mode without a notice, because nothing CHANGED. */
  modeChanges: number;
  /** MAJLIS-44 (D-34, P-17b): the latest seat whose SPEAKING language a seat_updated changed, numbered in the order this
   * client applied them. Each shows one notice (UI-C12's slot, lowest rank); room_state never sets it (nothing CHANGED
   * that this client saw), and a seat_updated that changes anything else (mute, Listen, reading) never does. */
  speakChange: { n: number; seat: string; lang: string } | null;
  /** MAJLIS-64 (D-43): THIS seat's own listen mode, from joined/resumed (`translated`, the server's default, until
   * then), and from the person's choice in the Listen sheet (UI-C21): set_listen_mode has no reply (docs/protocol.md),
   * so the choice is applied here as it is sent, and the next joined/resumed carries the server's record. */
  listenMode: ListenMode;
  /** MAJLIS-64: room_state.voice - voicing is on for this deployment (false: POST /api/voice is a 404, no Voice). */
  voiceOn: boolean;
  /** MAJLIS-64 (D-43, G13): the server answered 429 voice_capped - the spend cap is reached, so the interpreter
   * queue has stopped auto-playing and voice is on tap only. A QUIET state: the bar's one line "text only, voice on
   * tap". Cleared by a new join (a new room model). */
  voiceCapped: boolean;
  /** MAJLIS-37 (P-24, UI-C30): the room's door is locked (room_state.locked, then room_updated) - shown to EVERY seat. */
  locked: boolean;
  /** MAJLIS-126 (P-36, UI-C36): the room's listing, its name (null: never listed) and its listing handle (null while
   * unlisted), from room_state then room_updated - every seat's header says 'Listed' while it is not 'unlisted'. */
  listing: Listing;
  roomName: string | null;
  handle: string | null;
  /** MAJLIS-137 (P-38, D-57): room_state.passive - one person listening to the room around this phone (UI-C38/C39). */
  passive: boolean;
  /** P-19 (MAJLIS-89): this join was handed `n` earlier phrases at wall-clock ms `at` - the canvas shows the marker
   * above them and the "live from here" divider below them. null: a canvas that began empty, or an older server. */
  earlier: { n: number; at: number } | null;
  /** MAJLIS-113 (P-34, UI-C33): this connection is in the isolator's compare mode, from room time `since` (the server's
   * compare_mode). null on every other phone, and until compare_mode arrives after a joined/resumed. */
  compare: { since: number } | null;
  /** MAJLIS-113: the comparison phrases this phone was sent, by id. Kept across a resume (they are this phone's own
   * view); a new join starts a new model. Never stored anywhere else, never translated or voiced. */
  comparePhrases: Record<string, ComparePhraseData>;
}

export const emptyRoom = (): RoomModel => ({
  seats: {},
  seatOrder: [],
  bubbles: {},
  audioMode: "live",
  maxSeats: 8,
  mySeatId: null,
  roomWallEpoch: null,
  landedSeq: 0,
  clockAt: 0,
  engine: "up",
  translations: {},
  readSince: null,
  modeChanges: 0,
  speakChange: null,
  listenMode: "translated",
  voiceOn: false,
  voiceCapped: false,
  locked: false,
  listing: "unlisted",
  roomName: null,
  handle: null,
  passive: false,
  earlier: null,
  compare: null,
  comparePhrases: {},
});

export type RoomAction =
  | { type: "me"; seat: Seat; listenMode?: ListenMode }
  | { type: "voice_capped" }
  | { type: "listen_mode"; mode: ListenMode }
  | { type: "room_state"; data: RoomStateData }
  | { type: "engine"; state: "up" | "down" }
  | { type: "translation"; data: TranslationData }
  | { type: "read_lang"; now: number }
  | { type: "compare_mode"; since: number }
  | { type: "compare_phrase"; data: ComparePhraseData }
  | { type: "earlier"; phrases: EarlierPhrase[]; now: number; wall: number }
  | { type: "event"; event: RoomEvent; now: number; wall: number };

function upsertSeat(m: RoomModel, seat: Seat, present: boolean): RoomModel {
  let order = m.seatOrder;
  if (!order.includes(seat.id)) order = [...order, seat.id];
  const colour = order.indexOf(seat.id) % SEAT_COLOURS;
  return { ...m, seatOrder: order, seats: { ...m.seats, [seat.id]: { ...seat, colour, present } } };
}

function withBubble(m: RoomModel, id: string, f: (b: Bubble) => Bubble): RoomModel {
  const b = m.bubbles[id];
  return b ? { ...m, bubbles: { ...m.bubbles, [id]: f(b) } } : m;
}

export function reduce(m: RoomModel, a: RoomAction): RoomModel {
  switch (a.type) {
    case "me":
      // colours follow the room's join order, which room_state (right after) carries: do not take a
      // slot here, or this device would always see itself first
      // MAJLIS-113: compare mode is per connection - joined/resumed clears it, and its compare_mode (if any) follows
      return { ...m, mySeatId: a.seat.id, listenMode: a.listenMode ?? m.listenMode, compare: null };
    case "voice_capped":
      return m.voiceCapped ? m : { ...m, voiceCapped: true };
    case "listen_mode":
      return m.listenMode === a.mode ? m : { ...m, listenMode: a.mode };
    case "room_state": {
      let next: RoomModel = {
        ...m,
        audioMode: a.data.audio_mode,
        maxSeats: a.data.max_seats,
        engine: a.data.engine,
        voiceOn: a.data.voice === true,
        locked: a.data.locked === true,
        listing: a.data.listing ?? "unlisted",
        roomName: a.data.name ?? null,
        handle: a.data.handle ?? null,
        passive: a.data.passive === true, // MAJLIS-137 (P-38, additive)
      };
      const present = new Set(a.data.seats.map((s) => s.id));
      for (const s of a.data.seats) next = upsertSeat(next, s, true);
      const seats = { ...next.seats };
      for (const id of Object.keys(seats)) if (!present.has(id)) seats[id] = { ...seats[id], present: false };
      return { ...next, seats };
    }
    case "engine":
      return m.engine === a.state ? m : { ...m, engine: a.state };
    case "translation": {
      // Stored whatever this seat reads now: it is keyed by language, and a change back shows it again (T-307).
      const d = a.data;
      const mine = { ...(m.translations[d.segment_id] ?? {}), [d.lang]: d.text };
      return { ...m, translations: { ...m.translations, [d.segment_id]: mine } };
    }
    case "read_lang":
      return { ...m, readSince: a.now };
    case "compare_mode":
      return { ...m, compare: { since: a.since } };
    case "compare_phrase":
      // P-34: the server sends it to the compared seat's own connection only; anything else is not this phone's
      if (a.data.seat !== m.mySeatId) return m;
      return { ...m, comparePhrases: { ...m.comparePhrases, [a.data.id]: a.data } };
    case "earlier":
      return applyEarlier(m, a.phrases, a.now, a.wall);
    case "event":
      return { ...applyEvent(m, a.event, a.now, a.wall), clockAt: a.now };
  }
}

/** P-19 (MAJLIS-89): the phrases a join was handed become landed bubbles above the live edge, oldest first.
 *  - A phrase this model already holds is left exactly as it is: the same phrase can never be drawn twice (a join
 *    again after a lost token hands over phrases the canvas already shows, possibly newer than the list).
 *  - A speaker this client has not seen (a seat that has since left) is added NOT present: named and coloured on its
 *    phrases, absent from the seat stack.
 *  - Its time comes from this phone's clock: `wall - ago_s`. The room's wall epoch is taken from it when unknown.
 *  - Its translation, when the server sent one, is stored like a P-10; when it did not, the slot waits as for a
 *    phrase that just became final (TRANSLATION_WAIT_MS) - the server's D-45 (a) backfill sends it as P-10.
 *  - landedIndex counts them into the last TRANSLATION_WINDOW phrases, as the server's window does, and they are
 *    not "new" (landedSeq is unchanged: the jump pill counts live phrases only).
 *  - The marker and divider are drawn only when the list is the first thing on the canvas. */
function applyEarlier(m0: RoomModel, phrases: EarlierPhrase[], now: number, wall: number): RoomModel {
  const fresh = Object.keys(m0.bubbles).length === 0;
  let m = m0;
  const bubbles = { ...m.bubbles };
  const translations = { ...m.translations };
  let added = 0;
  phrases.forEach((p, i) => {
    const s = p.segment;
    if (!m.seats[p.speaker.id]) m = upsertSeat(m, p.speaker, false);
    if (m.roomWallEpoch === null) m = { ...m, roomWallEpoch: wall - (s.t0 + p.ago_s) * 1000 };
    if (p.translation && translations[s.id]?.[p.translation.lang] === undefined) {
      translations[s.id] = { ...(translations[s.id] ?? {}), [p.translation.lang]: p.translation.text };
    }
    if (bubbles[s.id]) return;
    bubbles[s.id] = {
      id: s.id,
      seat: s.seat,
      t0: s.t0,
      phase: "landed",
      startedAt: now,
      endedAt: now,
      segment: s,
      revised: false,
      revisionCount: 0,
      hadLive: false,
      wall: wall - p.ago_s * 1000,
      audioMs: p.audio_ms ?? undefined,
      finalAt: now,
      landedIndex: m0.landedSeq - (phrases.length - 1 - i),
      earlier: fresh,
    };
    added += 1;
  });
  if (added === 0) return { ...m, translations };
  return { ...m, bubbles, translations, earlier: fresh ? { n: added, at: wall } : m.earlier };
}

function applyEvent(m0: RoomModel, ev: RoomEvent, now: number, wall: number): RoomModel {
  const m = m0.roomWallEpoch === null ? { ...m0, roomWallEpoch: wall - ev.t * 1000 } : m0;
  const wallAt = (t0: number) => (m.roomWallEpoch ?? wall) + t0 * 1000;
  switch (ev.type) {
    case "seat_joined":
      return upsertSeat(m, ev.data.seat, true);
    case "seat_updated": {
      const before = m.seats[ev.data.seat.id];
      const next = upsertSeat(m, ev.data.seat, true);
      if (!before || before.speak_lang === ev.data.seat.speak_lang) return next;
      const n = (m.speakChange?.n ?? 0) + 1;
      return { ...next, speakChange: { n, seat: ev.data.seat.id, lang: ev.data.seat.speak_lang } };
    }
    case "seat_left": {
      const s = m.seats[ev.data.seat_id];
      return s ? { ...m, seats: { ...m.seats, [s.id]: { ...s, present: false } } } : m;
    }
    case "speech_start": {
      const d = ev.data;
      if (m.bubbles[d.segment_id]) return m;
      const bubble: Bubble = {
        id: d.segment_id,
        seat: d.seat,
        t0: d.t0,
        phase: "speaking",
        startedAt: now,
        revised: false,
        revisionCount: 0,
        hadLive: true,
        wall: wallAt(d.t0),
      };
      return { ...m, bubbles: { ...m.bubbles, [d.segment_id]: bubble } };
    }
    case "speech_end":
      return withBubble(m, ev.data.segment_id, (b) =>
        b.phase === "speaking" ? { ...b, phase: "writing", endedAt: now, durationMs: ev.data.duration } : b,
      );
    case "segment": {
      const s = ev.data;
      const prev = m.bubbles[s.id];
      const final = s.state === "final" ? { finalAt: now } : {};
      const landedIndex = m.landedSeq + 1;
      const landed: Bubble = prev
        ? { ...prev, phase: "landed", segment: s, t0: s.t0, endedAt: prev.endedAt ?? now, landedIndex, ...final }
        : {
            id: s.id,
            seat: s.seat,
            t0: s.t0,
            phase: "landed",
            startedAt: now,
            endedAt: now,
            segment: s,
            revised: false,
            revisionCount: 0,
            hadLive: false,
            wall: wallAt(s.t0),
            landedIndex,
            ...final,
          };
      return { ...m, landedSeq: m.landedSeq + 1, bubbles: { ...m.bubbles, [s.id]: landed } };
    }
    case "segment_dropped":
      // Every reason removes the bubble quietly and none is ever shown (ruling 1, MAJLIS-73); only the voice gate's
      // removal fades, because that bubble was a voice the reader may have watched for seconds (ruling 2).
      return withBubble(m, ev.data.segment_id, (b) => ({
        ...b,
        phase: "dropped",
        fadeFrom: ev.data.reason === "other_speaker" ? now : undefined,
      }));
    case "room_updated": {
      // MAJLIS-37 (P-24): the server's record of the door; no notice (the header carries it). MAJLIS-126 (P-36): a
      // listing change carries `listing` with its name and handle (absent = null); an absent field is unchanged.
      const d = ev.data;
      const next = d.locked === undefined ? m : { ...m, locked: d.locked };
      return d.listing === undefined
        ? next
        : { ...next, listing: d.listing, roomName: d.name ?? null, handle: d.handle ?? null };
    }
    case "audio_mode_changed":
      // MAJLIS-63 (P-17, UI-C17): the server's record of the room's mode, and one notice for the change.
      return { ...m, audioMode: ev.data.mode, modeChanges: m.modeChanges + 1 };
    case "segment_failed":
      return withBubble(m, ev.data.segment_id, (b) => ({ ...b, phase: "failed", endedAt: b.endedAt ?? now }));
    case "voice_named": {
      // MAJLIS-65 (P-16, D-18): the name applies to the whole session - EVERY bubble of that voice relabels, past ones
      // included, and no other bubble changes (a later segment of the voice carries the name itself).
      const { voice_id, display_name } = ev.data;
      let bubbles = m.bubbles;
      for (const b of Object.values(m.bubbles)) {
        const v = b.segment?.voice;
        if (!b.segment || !v || v.id !== voice_id || v.display_name === display_name) continue;
        if (bubbles === m.bubbles) bubbles = { ...m.bubbles };
        bubbles[b.id] = { ...b, segment: { ...b.segment, voice: { ...v, display_name } } };
      }
      return bubbles === m.bubbles ? m : { ...m, bubbles };
    }
    case "revision": {
      // EM-20: a revision always closes a provisional segment. Only changed=true is a visible revision
      // (UI-M3 shimmer and tag); changed=false returns the line to full ink quietly.
      const r = ev.data;
      return withBubble(m, r.id, (b) =>
        b.segment
          ? {
              ...b,
              segment: { ...b.segment, text: r.text, lang: r.lang, conf: r.conf, state: "final" },
              revised: b.revised || r.changed,
              revisionCount: b.revisionCount + (r.changed ? 1 : 0),
              finalAt: now, // D-17: the verdict makes the text final, and only now is it translated
            }
          : b,
      );
    }
    case "audio_ready":
      // UI-C5, EM-23: per-bubble readiness. The server sends it after a segment or segment_failed, never for a dropped
      // phrase; a dropped bubble is gone from the canvas, so it never takes one either.
      return withBubble(m, ev.data.segment_id, (b) =>
        b.phase === "dropped" ? b : { ...b, audioMs: ev.data.duration_ms },
      );
  }
}

/** Seats in the header and the seat list: present ones, in join order. */
export function presentSeats(m: RoomModel): SeatView[] {
  return m.seatOrder.map((id) => m.seats[id]).filter((s): s is SeatView => !!s && s.present);
}

/** Seats with a phrase being spoken right now (UI-C7). */
export function speakingSeats(m: RoomModel): Set<string> {
  const out = new Set<string>();
  for (const b of Object.values(m.bubbles)) if (b.phase === "speaking") out.add(b.seat);
  return out;
}

/** The most recent speaker still speaking, for the jump pill (UI-C11). */
export function latestSpeaker(m: RoomModel): SeatView | null {
  let best: Bubble | null = null;
  for (const b of Object.values(m.bubbles)) if (b.phase === "speaking" && (!best || b.t0 > best.t0)) best = b;
  return best ? (m.seats[best.seat] ?? null) : null;
}

/** Bubbles in canvas order: by t0, then by id for a stable order. */
export function orderedBubbles(m: RoomModel): Bubble[] {
  return Object.values(m.bubbles).sort((a, b) => a.t0 - b.t0 || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** What a landed bubble shows under its original (UI-C4, MAJLIS-49): nothing, the translation, or the pending slot. */
export type TranslationView = { kind: "none" } | { kind: "pending" } | { kind: "text"; lang: string; text: string };

/** The translation slot of one bubble for a reader of `readLang` at `now` (local clock, ms).
 *  - nothing when the phrase is in the reader's own language (L-6: nobody pays for it);
 *  - the text once P-10 has arrived for this language;
 *  - pending while the text is provisional (D-17), and after it became final - or after a reading-language change
 *    that the server backfills (the last TRANSLATION_WINDOW phrases) - for at most TRANSLATION_WAIT_MS;
 *  - otherwise nothing: a failure sends no message (the original stands), and a phrase older than the window is
 *    not translated into a language chosen after it (consultant ruling (a), 2026-09-22). */
export function translationView(m: RoomModel, b: Bubble, readLang: string, now: number): TranslationView {
  const seg = b.segment;
  if (!seg || b.phase !== "landed" || sameLanguage(seg.lang, readLang)) return { kind: "none" };
  const text = m.translations[b.id]?.[readLang];
  if (text !== undefined) return { kind: "text", lang: readLang, text };
  if (seg.state === "provisional") return { kind: "pending" };
  const since = pendingSince(m, b);
  return since !== null && now - since < TRANSLATION_WAIT_MS ? { kind: "pending" } : { kind: "none" };
}

/** When this bubble's translation began to be owed: the moment it became final, or - for a change of reading
 * language after that - the change, if the phrase is inside the window the server backfills. */
function pendingSince(m: RoomModel, b: Bubble): number | null {
  if (b.finalAt === undefined) return null;
  // a change at the same instant as the phrase became final came after it (actions apply in order)
  if (m.readSince === null || m.readSince < b.finalAt) return b.finalAt;
  const inWindow = b.landedIndex !== undefined && b.landedIndex > m.landedSeq - TRANSLATION_WINDOW;
  return inWindow ? m.readSince : null;
}

/** The next moment a pending translation slot gives up by time alone, so the canvas re-renders exactly then. */
export function nextTranslationBoundary(m: RoomModel, readLang: string, now: number): number | null {
  let next: number | null = null;
  for (const b of Object.values(m.bubbles)) {
    if (translationView(m, b, readLang, now).kind !== "pending" || b.segment?.state !== "final") continue;
    const since = pendingSince(m, b);
    if (since === null) continue;
    const at = since + TRANSLATION_WAIT_MS;
    if (at > now && (next === null || at < next)) next = at;
  }
  return next;
}

// ---------------------------------------------------------------- MAJLIS-113: the isolator's compare mode (UI-C33/34)

/** What sits under one of THIS seat's bubbles in compare mode (UI-C33, UI-C34): the waiting line, the isolator's text
 * (its phrases, in time order), or the dashed "(nothing)" placeholder. */
export type CompareView =
  | { kind: "pending" }
  | { kind: "text"; phrases: ComparePhraseData[] }
  | { kind: "absent" };

/** PAIRING (UI-C33): each comparison phrase belongs to the ONE bubble of this seat with a segment whose [t0, t1] it
 * overlaps most (a tie: the earlier bubble). A phrase overlapping none is held, never drawn (it has no partner to sit
 * under). Pure: recomputed from the model, so a phrase that arrived before its partner's segment pairs once it lands. */
export function comparePartners(m: RoomModel): Record<string, ComparePhraseData[]> {
  const out: Record<string, ComparePhraseData[]> = {};
  const mine = Object.values(m.bubbles).filter((b) => b.seat === m.mySeatId && b.segment && !b.earlier);
  for (const r of Object.values(m.comparePhrases)) {
    let best: Bubble | null = null;
    let bestOverlap = 0;
    for (const b of mine) {
      const s = b.segment!;
      const overlap = Math.min(s.t1, r.t1) - Math.max(s.t0, r.t0);
      if (overlap <= 0) continue;
      if (!best || overlap > bestOverlap || (overlap === bestOverlap && s.t0 < best.segment!.t0)) {
        best = b;
        bestOverlap = overlap;
      }
    }
    if (best) (out[best.id] ??= []).push(r);
  }
  for (const list of Object.values(out)) list.sort((a, b) => a.t0 - b.t0 || (a.id < b.id ? -1 : 1));
  return out;
}

/** THE SLOT UNDER ONE BUBBLE at `now` (local clock, ms), or null for no slot at all:
 *  - null unless this phone is in compare mode and the bubble is THIS seat's, live or landed (never failed, dropped
 *    or a join's history) - every other phone, and every other bubble, is exactly as it was;
 *  - the isolator's text ONLY under a FINAL phrase with a partner (red on finals only, UI-C34);
 *  - for a phrase that started at or after `since`: the waiting line while it is live or provisional, and after it
 *    became final until COMPARE_ABSENT_MS has passed with no partner - then "(nothing)" (ABSENCE, UI-C34). A partner
 *    arriving later replaces the placeholder;
 *  - a phrase that started before `since` (spoken before this connection could be sent its comparison) gets its red
 *    when it has one, and otherwise nothing: its absence cannot be decided. */
export function compareView(
  m: RoomModel,
  b: Bubble,
  partners: Record<string, ComparePhraseData[]>,
  now: number,
): CompareView | null {
  if (!m.compare || b.seat !== m.mySeatId || b.earlier) return null;
  if (b.phase === "failed" || b.phase === "dropped") return null;
  const final = b.phase === "landed" && b.segment?.state === "final";
  const found = partners[b.id];
  if (final && found && found.length > 0) return { kind: "text", phrases: found };
  if (b.t0 < m.compare.since) return null;
  if (final && b.finalAt !== undefined && now - b.finalAt >= COMPARE_ABSENT_MS) return { kind: "absent" };
  return { kind: "pending" };
}

/** The next moment a waiting slot turns into "(nothing)" by time alone, so the canvas re-renders exactly then. */
export function nextCompareBoundary(
  m: RoomModel,
  partners: Record<string, ComparePhraseData[]>,
  now: number,
): number | null {
  let next: number | null = null;
  for (const b of Object.values(m.bubbles)) {
    if (b.finalAt === undefined || compareView(m, b, partners, now)?.kind !== "pending") continue;
    if (b.phase !== "landed" || b.segment?.state !== "final") continue;
    const at = b.finalAt + COMPARE_ABSENT_MS;
    if (at > now && (next === null || at < next)) next = at;
  }
  return next;
}
